'use client';

import { useEffect, useRef, useState } from 'react';

import { Minimize2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

interface FullscreenExitOverlayProps {
  onExit: () => void;
}

export function FullscreenExitOverlay({ onExit }: FullscreenExitOverlayProps) {
  const t = useTranslations();
  const [hovered, setHovered] = useState(false);
  const hideTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (hideTimerRef.current !== null) {
        window.clearTimeout(hideTimerRef.current);
      }
    },
    [],
  );

  const handleEnter = () => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setHovered(true);
  };

  // Small delay before hiding so brief cursor wobbles near the zone boundary
  // don't restart the transition each time and look jittery.
  const handleLeave = () => {
    if (hideTimerRef.current !== null) {
      window.clearTimeout(hideTimerRef.current);
    }
    hideTimerRef.current = window.setTimeout(() => {
      setHovered(false);
      hideTimerRef.current = null;
    }, 1200);
  };

  return (
    <div
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      className="fixed left-0 right-0 top-0 z-50 h-14"
    >
      {/* Centering wrapper isolates the -50% translate from the animated translateY */}
      <div className="absolute left-1/2 top-0 -translate-x-1/2">
        <button
          type="button"
          onClick={onExit}
          aria-label={t('get-feedback-workbench-exitFullscreen')}
          style={{ willChange: 'transform' }}
          className={`flex items-center gap-1.5 rounded-b-full border-x border-b border-border bg-background px-4 py-1.5 text-xs font-medium text-foreground shadow-md transition-transform duration-200 ease-out hover:bg-muted ${
            hovered ? 'translate-y-0' : '-translate-y-[70%]'
          }`}
        >
          <Minimize2 className="h-3.5 w-3.5" />
          {t('get-feedback-workbench-exitFullscreen')}
        </button>
      </div>
    </div>
  );
}
